import { UUID, uuidv7 } from "uuidv7"

import type { Conversation } from "~/models/polyschema/Conversation"

import { parseConversation } from "~/utils/flatbuffers/conversation"

import type { ConversationBuf } from "../generated/conversation"

import { conversationMember, dateFromTimestampUuid, groupFor, Property, PropertyGroup, PropertySchema, PropertyStatus } from "../entity"
import { InterfaceSelection } from "../entity/InterfaceSelection"
import { InterfaceFile } from "../file/InterfaceFile"
import { FileUserRole } from "../file/properties/FileUserRole"
import { DatagramErrorTypeBuf } from "../generated/datagram"
import { sendConversationCloseRequests } from "../handlers/conversation/close"
import { sendConversationPropertiesRequests } from "../handlers/conversation/properties"
import { sendConversationSubscribeRequests } from "../handlers/conversation/subscribe"
import { sendConversationUpdateRequests } from "../handlers/conversation/update"

export class InterfaceConversation {
  _conversation: Conversation

  constructor(conversation: Conversation) {
    this._conversation = conversation
  }

  static from(conversation?: Conversation) {
    if (conversation)
      return new InterfaceConversation(conversation)
  }

  static fromBuf(buf: ConversationBuf): InterfaceConversation {
    return new InterfaceConversation(parseConversation(buf))
  }

  /**
   * NOTE: ConversationEvent is just an alias for Conversation (for now)
   */
  static fromEvent(event: ConversationEvent): InterfaceConversation {
    return new InterfaceConversation(event)
  }

  static clientPreHash() {
    return `00040000-${uuidv7()}`
  }

  /**
   * When we don't have enough info about a conversation to type it. Should only be used for, e.g.
   * moving conversations or other clear placeholder tasks. Should not be used to create a conversation
   * in the UI as this conversation will lack other necessary properties
   */
  static blank(conversationId: string) {
    const selection = InterfaceSelection.nothing().inner()
    return new InterfaceConversation({ conversationId, properties: {}, selection })
  }

  inner(): Readonly<Conversation> {
    return this._conversation
  }

  get conversationId(): string {
    return this._conversation.conversationId
  }

  static async idFromPrehash(preHash: string) {
    const [apiId, keyId] = partition(preHash, "-")
    const hash = await window.crypto.subtle.digest("SHA-256", UUID.parse(keyId).bytes)
    const uuid = UUID.ofInner(new Uint8Array(hash.slice(0, 16)))
    return `${apiId}-${uuid.toString()}` as Property
  }

  get properties() {
    return this._conversation.properties
  }

  get selection() {
    return new InterfaceSelection(this._conversation.selection)
  }

  property(id: Property): ConversationProperty | undefined {
    return this._conversation.properties?.[id]
  }

  /**
   * We place "blank" conversations in place of real ones in the UI while we are retrieving data, if
   * we know the conversation Id already. One place we use this is in the sidebar.
   * @returns whether this conversation is a blank placeholder for further data retrieval
   */
  isBlank() {
    return Object.keys(this._conversation.properties).length === 0
  }

  /**
   * Staleness refers to whether our locally cached copy of the conversation is out of date, such as
   * when we are retrieving a new version from the server. Technically, only properties can
   * be stale, but for this function we just return true if any are stale
   *
   * @returns whether any of the properties of this conversation are stale
   */
  isCurrentAsOfEpoch(cacheEpochId: number | undefined) {
    if (!isDefined(cacheEpochId))
      return false
    for (const property of Object.values(this._conversation.properties)) {
      if (property?.epochId !== cacheEpochId)
        return false
    }
    return true
  }

  /**
   * Pending is a property which is at a higher version than the server, waiting to push its
   * updates to the server. This function returns true if any of the properties are pending.
   *
   * @returns whether any of the properties of this conversation are pending
   */
  isPending() {
    for (const property of Object.values(this._conversation.properties))
      if (enumEq(property!.status, PropertyStatus.StatusPending))
        return true
    return false
  }

  addProperty(property: ConversationProperty) {
    this._conversation.properties[property.propertyId] = property
  }

  /**
   * Merges two interface conversation's based on their properties. The "newer" one doesn't have
   * to be newer as such, but we take its properties and path by default, so it should
   * be the more recently retrieved conversation if possible.
   */
  mergeWithNewer(newer?: InterfaceConversation) {
    if (!newer)
      return this
    if (newer.conversationId !== this.conversationId)
      throw new Error("Cannot merge conversations with different conversation ids!")

    // merge the selections first
    this.selection.mergedWith(newer.selection)

    for (const prop of Object.values(newer._conversation.properties)) {
      const thisProp = this._conversation.properties[prop!.propertyId]
      if (!prop)
        continue
      if (thisProp && (thisProp.epochId ?? 0) > (prop.epochId ?? 0))
        continue
      if (thisProp && (thisProp.version ?? 0) > (prop.version ?? 0))
        continue
      if (thisProp && (thisProp.timestamp) > (prop.timestamp!))
        continue
      if (thisProp && thisProp.status === PropertyStatus.StatusCurrent && prop.status !== PropertyStatus.StatusCurrent)
        continue
      this._conversation.properties[prop!.propertyId] = prop
    }
    // Generate a new outer class to ensure we induce reactivity signals
    return new InterfaceConversation(this._conversation)
  }

  /**
   * Adds a pending owner property to this conversation
   */
  withSelfOwner(userId: string, timestamp: string) {
    this.addProperty({
      conversationId: this.conversationId,
      propertyId: conversationMember(userId),
      author: userId,
      schema: PropertySchema.RoleUuidValue,
      version: 0,
      sharedWithUserId: userId,
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.RoleUuidValue, role: FileUserRole.newOwner().inner() },
      timestamp,
    })
    return this
  }

  withCreatedAt(userId: string, timestamp: string) {
    this.addProperty({
      conversationId: this.conversationId,
      propertyId: Property.CONVERSATION_CREATED_AT,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis: InterfaceFile.asTimestampMillis(timestamp) },
      timestamp,
      version: 0,
      schema: PropertySchema.UtcTimestampMillisValue,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    return this
  }

  memberProperties(): ConversationProperty[] {
    return Object
      .values(this.properties)
      .map(p => p!)
      .filter(p => groupFor(p.propertyId as Property) === PropertyGroup.ConversationSharingUsersMetaProperty)
  }

  members(): Record<string, FileUserRole> {
    const members: Record<string, FileUserRole> = {}
    for (const property of this.memberProperties()) {
      if (property.deleted)
        continue
      if (!property.sharedWithUserId)
        continue
      if (property.value.type !== PropertySchema.RoleUuidValue)
        continue
      const role = new FileUserRole(property.value.role)
      members[property.sharedWithUserId] = role
    }
    return members
  }

  getUserRole(userId: string): FileUserRole | undefined {
    const property = this.property(conversationMember(userId))
    if (property && !property.deleted && property.value.type === PropertySchema.RoleUuidValue)
      return new FileUserRole(property.value.role)
  }

  isUserRemoved(userId: string): boolean {
    const property = this.property(conversationMember(userId))
    return property?.deleted === true
  }

  /**
   * The conversation's transcript is just a set of conversation properties, sorted
   * by timestamp in ascending order
   */
  turnProperties() {
    return Object
      .values(this.properties)
      .map(p => p!)
      .filter(p => groupFor(p.propertyId as Property) === PropertyGroup.ConversationTurnsMetaProperty)
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
  }

  /**
   * NOTE: This does not guarantee that any particular turn will be returned, just the ones
   * that are in this object.
   */
  turns() {
    return this
      .turnProperties()
      .map(p => p.value.type === PropertySchema.ConversationTurnValue ? p.value.turn : undefined)
      .filter(p => p!)
      .map(p => p!)
  }

  lastUpdated() {
    const property = Object.values(this.properties)
      .map(p => p!)
      .filter(p => groupFor(p.propertyId as Property) === PropertyGroup.ConversationSharingUsersMetaProperty)
      .reduce((latest, current) => {
        if (!latest || (current.timestamp > latest.timestamp))
          return current
        return latest
      }, undefined as ConversationProperty | undefined)
    if (!property)
      throw new Error("No conversation members, cannot know last updated time")
    return dateFromTimestampUuid(property.timestamp)
  }

  isEmpty() {
    return Object
      .values(this.properties)
      .map(p => p!)
      .find(p => groupFor(p.propertyId as Property) === PropertyGroup.ConversationTurnsMetaProperty) === undefined
  }

  /**
   * The title of this conversation (which could be un-set)
   */
  title() {
    const titleProperty = this.property(Property.CONVERSATION_USER_TITLE)
    if (titleProperty && titleProperty.value.type === PropertySchema.NonEmptyStringValue) {
      return titleProperty.value.nonEmptyString
    }
  }

  /**
   * A conversation is closed if the last message in the transcript is an 'ended' response
   */
  isClosed() {
    if (this.selection.hasEntireGroup(PropertyGroup.ConversationTurnsMetaProperty)) {
      const turn = last(this.turns())
      if (!isDefined(turn))
        return true
      if ("agent" in turn && "ended" in turn.agent)
        return true
      return false
    }
    // cannot know if we don't have the selection. Default to assuming it is.
    return true
  }

  /**
   * TODO: throwing errors here is not an ergonomic way to handle the result. Instead, we
   * could return an option or return a tuple of [error, result]
   */
  close() {
    const { resolve, reject, promise: authoritative } = Promise.withResolvers<void>()
    const requestId = generateWorkerRequestId()
    const { addInterfaceEventListener, removeInterfaceEventListener } = useEvents()
    const onStop = (response: ConversationCloseResponseDatagram) => {
      if (response.error)
        reject(response.error)
      else
        resolve()
      removeInterfaceEventListener("conversationCloseResponse", onStop)
    }
    addInterfaceEventListener(
      "conversationCloseResponse",
      onStop,
      { requestId },
    )
    sendConversationCloseRequests([{
      requestId,
      conversationId: this.conversationId,
      offline: false,
    }])
    return { authoritative }
  }

  /**
   * FIXME: This style of writing causes there to be uncaught errors in promises because a
   * user that handles the authoritative and not cached promise won't catch the error.
   */
  getProperties(selection?: InterfaceSelection) {
    const { addInterfaceEventListener, removeInterfaceEventListener } = useEvents()
    const { isOffline, isConversationFresh } = useCache()

    const {
      resolve: cachedResolve,
      reject: cachedReject,
      promise: cached,
    } = Promise.withResolvers<InterfaceConversation>()
    const {
      resolve: authoritativeResolve,
      reject: authoritativeReject,
      promise: authoritative,
    } = Promise.withResolvers<InterfaceConversation>()
    // retrieve this conversation's properties and provide a cached and authoritative
    // promise to wait on either response
    const requestId = generateWorkerRequestId()

    const onProperties = (response: ConversationPropertiesResponseDatagram) => {
      if (response.offline && response.error && isOffline()) {
        cachedReject(response.error)
        authoritativeReject(response.error)
      }
      else if (response.offline && response.error) {
        // TODO: this cached rejection causes headaches in testing. We need to migrate to a
        // better error handling style, by, e.g. adding the error in the response type
        // rather than using the try/catch style which is pretty awful.
        // cachedReject(response.error)
        return
      }
      else if (
        response.offline
        && isDefined(response.conversation)
        && isConversationFresh(new InterfaceConversation(response.conversation))
      ) {
        cachedResolve(new InterfaceConversation(response.conversation))
        authoritativeResolve(new InterfaceConversation(response.conversation))
      }
      else if (response.offline && isDefined(response.conversation) && isOffline()) {
        cachedResolve(new InterfaceConversation(response.conversation))
        authoritativeReject({ details: "Failed to get conversation properties", type: DatagramErrorTypeBuf.CurrentlyOffline })
      }
      else if (response.offline && isDefined(response.conversation)) {
        cachedResolve(new InterfaceConversation(response.conversation))
        // not fresh, so we resolve the cache only
        return
      }
      else if (!response.offline && response.error) {
        cachedReject(response.error)
        authoritativeReject(response.error)
      }
      else if (!response.offline && isDefined(response.conversation)) {
        cachedResolve(new InterfaceConversation(response.conversation))
        authoritativeResolve(new InterfaceConversation(response.conversation))
      }
      else {
        console.error("Received conversation properties in unknown state", response)
        cachedReject({ type: DatagramErrorTypeBuf.InternalServerError, details: "Received conversation properties in unknown state" })
        authoritativeReject({ type: DatagramErrorTypeBuf.InternalServerError, details: "Received conversation properties in unknown state" })
      }
      removeInterfaceEventListener("conversationPropertiesResponse", onProperties)
    }

    addInterfaceEventListener(
      "conversationPropertiesResponse",
      onProperties,
      { requestId },
    )

    selection = selection ?? InterfaceSelection.minimumConversationViewSelection()

    sendConversationPropertiesRequests([{
      requestId,
      conversationId: this.conversationId,
      selection: selection.inner(),
      offline: false,
    }])

    return { cached, authoritative }
  }

  updateConversationTitle(title: string) {
    const { isOffline } = useCache()
    const { state: myself } = useSelf()
    if (!myself.value.user)
      throw new Error("Cannot update conversation title without a user")
    const userId = myself.value.user.userId

    const titleProperty = this.property(Property.CONVERSATION_USER_TITLE)
    let version = 0
    if (titleProperty && !isOffline() && titleProperty.status === PropertyStatus.StatusCurrent)
      version = titleProperty.version + 1
    else if (titleProperty)
      version = titleProperty.version

    title = title.trim()
    if (!isDefined(title) || title.length === 0)
      return console.error("Cannot change to empty title")
    const conversation = InterfaceConversation.blank(this.conversationId)
    conversation.addProperty({
      conversationId: this.conversationId,
      propertyId: Property.CONVERSATION_USER_TITLE,
      value: {
        type: PropertySchema.NonEmptyStringValue,
        nonEmptyString: title,
      },
      version,
      schema: PropertySchema.NonEmptyStringValue,
      author: userId,
      timestamp: uuidv7(),
      status: PropertyStatus.StatusPending,
    })
    sendConversationUpdateRequests([{
      requestId: generateWorkerRequestId(),
      conversation: conversation.inner(),
      offline: isOffline(),
    }])
  }

  async sendInvite(userId: string, remove = false, timeoutMs = 5000) {
    const { waitForFirstResponse } = useEvents()
    const { state: myself } = useSelf()
    if (!myself.value.user)
      throw new Error("Cannot invite user without a user")
    const conversation = InterfaceConversation.blank(this.conversationId)
    conversation.addProperty({
      conversationId: this.conversationId,
      propertyId: conversationMember(userId),
      value: {
        type: PropertySchema.RoleUuidValue,
        role: FileUserRole.newEditor().inner(),
      },
      version: 0,
      deleted: remove,
      schema: PropertySchema.RoleUuidValue,
      author: myself.value.user.userId,
      timestamp: uuidv7(),
      sharedWithUserId: userId,
      status: PropertyStatus.StatusPending,
    })
    const requestId = generateWorkerRequestId()
    sendConversationUpdateRequests([{
      requestId,
      conversation: conversation.inner(),
      offline: false,
    }])
    const response = await withTimeout(
      waitForFirstResponse("conversationUpdateResponse", requestId),
      timeoutMs,
    )
    if (response.error || !isDefined(response.conversation))
      throw new Error("User not found")
  }

  async subscribe(timeoutMs = 5000) {
    const requestId = generateWorkerRequestId()
    sendConversationSubscribeRequests([{
      requestId,
      conversationId: this.conversationId,
      offline: false,
    }])
    const { waitForServerResponse } = useEvents()
    const response = await withTimeout(
      waitForServerResponse("conversationSubscribeResponse", requestId),
      timeoutMs,
    )
    return response
  }
}
